import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import GoBack from '../img/go-back-icn.svg';
import saveIcon from '../img/save-icn.svg';
import resetIcon from '../img/reset-button-icn.svg';
import { doRequest, userID } from '../../../../utils/helper';
import { toast } from 'react-toastify';
import './sizesNew.css';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSizes } from '../../../../redux/slices/SizeSlice';
import { defaultSize } from '../../../../data/sizeData';

const SizeInput = ({ value, onChange, disabled, hasError }) => {
	const [focused, setFocused] = useState(false);
	const inputRef = useRef(null);

	const handleIncrease = () => {
		if (disabled) return;
		const newValue = Math.max(0, (parseFloat(value) || 0) + 0.1);
		onChange(Number(newValue.toFixed(1)));
	};

	const handleDecrease = () => {
		if (disabled) return;
		const newValue = Math.max(0, (parseFloat(value) || 0) - 0.1);
		onChange(Number(newValue.toFixed(1)));
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (inputRef.current && !inputRef.current.contains(event.target)) {
				setFocused(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<div className={`input-container ${focused ? 'focused' : ''} ${disabled ? 'disabled' : ''} ${hasError ? 'error' : ''}`} ref={inputRef}>
			<input
				type='number'
				placeholder='Enter Value'
				value={value}
				step="0.1"
				min={1}
				max={80}
				disabled={disabled}
				onFocus={() => !disabled && setFocused(true)}
				onChange={(e) => !disabled && onChange(parseFloat(e.target.value))}
			/>
			{focused && !disabled && (
				<div className='button-container'>
					<button className='decrease' onClick={handleDecrease}></button>
					<button className='increase' onClick={handleIncrease}></button>
				</div>
			)}
		</div>
	);
};

const SizeSelectorUi = () => {
	const userId = userID();
	const dispatch = useDispatch();
	const sizes = useSelector(state => state.sizeDimension.sizes);
	const isLoading = useSelector(state => state.sizeDimension.isLoading);
	const error = useSelector(state => state.sizeDimension.error);
	const [isSaving, setIsSaving] = useState(false);
	const [localSizes, setLocalSizes] = useState([]);
	const [validationErrors, setValidationErrors] = useState({});

	// Sync localSizes with Redux sizes for editing
	useEffect(() => {
		setLocalSizes(sizes);
	}, [sizes]);

	useEffect(() => {
		dispatch(fetchSizes(userId));
	}, [dispatch, userId]);

	const validateSize = (size) => {
		const errors = {};
		const fields = ['width', 'height', 'halfSleeveWidth', 'halfSleeveHeight', 'fullSleeveWidth', 'fullSleeveHeight'];
		
		fields.forEach(field => {
			const value = size[field];
			if (!value || value === '') {
				errors[field] = 'Required';
			} else if (value < 1) {
				errors[field] = 'Minimum value is 1';
			} else if (value > 80) {
				errors[field] = 'Maximum value is 80';
			}
		});

		return errors;
	};

	const handleSizeChange = (index, field, value) => {
		const updatedSizes = [...localSizes];
		updatedSizes[index] = {
			...updatedSizes[index],
			[field]: value
		};
		setLocalSizes(updatedSizes);
		
		// Clear validation error for this field
		if (validationErrors[index]?.[field]) {
			const newErrors = { ...validationErrors };
			delete newErrors[index][field];
			if (Object.keys(newErrors[index]).length === 0) {
				delete newErrors[index];
			}
			setValidationErrors(newErrors);
		}
	};

	const createSize = async (sizeData) => {
		try {
			const payload = {
				data: sizeData.map(size => ({
					user_id: userId,
					type: "India",
					size_no: size.label,
					size_name: size.age,
					tshirt_width: size.width || 0,
					tshirt_height: size.height || 0,
					half_sleeve_width: size.halfSleeveWidth || 0,
					half_sleeve_height: size.halfSleeveHeight || 0,
					full_sleeve_width: size.fullSleeveWidth || 0,
					full_sleeve_height: size.fullSleeveHeight || 0
				}))
			};
			const response = await doRequest(payload, 'POST', 'size/save');
			if (response?.resultFlag === 1) {
				toast.success('Sizes created successfully');
				dispatch(fetchSizes(userId));
				return true;
			} else {
				toast.error(response?.message || 'Failed to create sizes');
				return false;
			}
		} catch (error) {
			toast.error('Failed to create sizes. Please try again.');
			return false;
		}
	};

	const updateSizeApi = async (size) => {
		try {
			if (!validateSize(size)) {
				return false;
			}
			const payload = {
				id: size.id,
				data: {
					user_id: userId,
					type: "India",
					size_no: size.label,
					size_name: size.age,
					tshirt_width: size.width,
					tshirt_height: size.height,
					half_sleeve_width: size.halfSleeveWidth,
					half_sleeve_height: size.halfSleeveHeight,
					full_sleeve_width: size.fullSleeveWidth,
					full_sleeve_height: size.fullSleeveHeight
				}
			};
			const response = await doRequest(payload, 'POST', 'size/update');
			if (response.resultFlag === 1) {
				return true;
			}
		} catch (error) {
			toast.error(`Failed to update size ${size.label}`);
			return false;
		}
	};

	const handleSaveChanges = async () => {
		if (isSaving) return;
		
		// Validate all sizes
		const allErrors = {};
		localSizes.forEach((size, index) => {
			const errors = validateSize(size);
			if (Object.keys(errors).length > 0) {
				allErrors[index] = errors;
			}
		});

		if (Object.keys(allErrors).length > 0) {
			setValidationErrors(allErrors);
			Object.entries(allErrors).forEach(([index, errors]) => {
				const size = localSizes[index];
				toast.error(
					<div>
						<strong>{`Size ${size.label} (${size.age}):`}</strong>
						<ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
							{Object.entries(errors).map(([field, error]) => (
								<li key={field} style={{ textAlign: 'left' }}>
									{field.charAt(0).toUpperCase() + field.slice(1)}: {error}
								</li>
							))}
						</ul>
					</div>
				);
			});
			return;
		}

		try {
			setIsSaving(true);
			// Separate sizes into new and existing ones
			const newSizes = localSizes.filter(size => !size.id);
			const existingSizes = localSizes.filter(size => size.id);
			// Create new sizes in bulk
			if (newSizes.length > 0) {
				const createSuccess = await createSize(newSizes);
				if (!createSuccess) {
					toast.error('Failed to create some sizes');
					return;
				}
			}
			// Update existing sizes one by one
			for (const size of existingSizes) {
				const updateSuccess = await updateSizeApi(size);
				if (!updateSuccess) {
					// toast.error(`Failed to update size ${size.label}`);
				}
			}
			toast.success('Sizes saved successfully');
			dispatch(fetchSizes(userId)); // Refresh the data
			setValidationErrors({}); // Clear all error states after successful save
		} catch (error) {
			toast.error('Failed to save changes');
		} finally {
			setIsSaving(false);
		}
	};

	const handleResetTeeshirtSizes = () => {
		setLocalSizes(prev =>
			prev.map((size, idx) => ({
				...size,
				width: defaultSize[idx].width,
				height: defaultSize[idx].height
			}))
		);
	};

	const handleResetHalfSleeveSizes = () => {
		setLocalSizes(prev =>
			prev.map((size, idx) => ({
				...size,
				halfSleeveWidth: defaultSize[idx].halfSleeveWidth,
				halfSleeveHeight: defaultSize[idx].halfSleeveHeight
			}))
		);
	};

	const handleResetFullSleeveSizes = () => {
		setLocalSizes(prev =>
			prev.map((size, idx) => ({
				...size,
				fullSleeveWidth: defaultSize[idx].fullSleeveWidth,
				fullSleeveHeight: defaultSize[idx].fullSleeveHeight
			}))
		);
	};

	return (
		<main className='artboard--main variation-main'>
			<div className='artboard--main__header'>
				<div className='artboard--main__header--left--back'>
					<Link to='/variation'>
						<img src={GoBack} alt="Go Back" />
					</Link>
				</div>
				<div className='artboard--main__header--right'>
					<button 
						className='save-changes-btn' 
						onClick={handleSaveChanges}
						disabled={isSaving || isLoading}
					>
						<img src={saveIcon} alt="Save" />
						{isSaving ? 'Saving...' : 'Save Changes'}
					</button>
				</div>
			</div>

			{error && (
				<div className="error-message">
					{error}
				</div>
			)}

			{isLoading ? (
				<div className="loading-message">
					Loading sizes...
				</div>
			) : (
				<section className='artboard--main__sizes--container newSizes'>
					<div className="artboard--main__sizes--item" id="size-sizes">
						<h2>Teeshirt Sizes</h2>
						<div className="artboard--main__sizes--item--box--head">
							<div className="artboard--main__sizes--item--box--head--list">
								<div className="data-size-size">Size</div>
							</div>
						</div>
						<div className="artboard--main__sizes--item--box--data">
							{localSizes.map((size, index) => (
								<div key={index} className="artboard--main__sizes--item--box--data--list">
									<div className={`data-size-size ${index === 0 ? 'selected' : ''}`}>
										<div className="data-size-blank">{size.label}</div>
										<div className="data-size-age">{size.age}</div>
									</div>
								</div>
							))}
						</div>
					</div>

					<div className="artboard--main__sizes--item" id="teeshirt-sizes">
						<h2>
							Teeshirt Sizes
							<button className="resetSizes" onClick={handleResetTeeshirtSizes} disabled={isSaving || isLoading}>
								<img src={resetIcon} alt="Reset Button Icon" /> Reset
							</button>
						</h2>
						<div className="artboard--main__sizes--item--box--head">
							<div className="artboard--main__sizes--item--box--head--list">
								<div className="data-size-width">Width <span>(in)</span></div>
								<div className="data-size-height">Height <span>(in)</span></div>
							</div>
						</div>
						<div className="artboard--main__sizes--item--box--data">
							{localSizes.map((size, index) => (
								<div key={index} className="artboard--main__sizes--item--box--data--list">
									<div className="data-size-width">
										<SizeInput
											value={size.width}
											onChange={(value) => handleSizeChange(index, 'width', value)}
											disabled={isSaving || isLoading}
											hasError={!!validationErrors[index]?.width}
										/>
									</div>
									<div className="data-size-height">
										<SizeInput
											value={size.height}
											onChange={(value) => handleSizeChange(index, 'height', value)}
											disabled={isSaving || isLoading}
											hasError={!!validationErrors[index]?.height}
										/>
									</div>
								</div>
							))}
						</div>
					</div>

					<div className="artboard--main__sizes--item" id="halfsleeve-sizes">
						<h2>Half Sleeve Sizes <button className="resetSizes" onClick={handleResetHalfSleeveSizes} disabled={isSaving || isLoading}><img src={resetIcon} alt='Reset Button Icon' /> Reset</button></h2>
						<div className="artboard--main__sizes--item--box--head">
							<div className="artboard--main__sizes--item--box--head--list">
								<div className="data-size-width">Width <span>(in)</span></div>
								<div className="data-size-height">Height <span>(in)</span></div>
							</div>
						</div>
						<div className="artboard--main__sizes--item--box--data">
							{localSizes.map((size, index) => (
								<div key={index} className="artboard--main__sizes--item--box--data--list">
									<div className="data-size-width">
										<SizeInput
											value={size.halfSleeveWidth}
											onChange={(value) => handleSizeChange(index, 'halfSleeveWidth', value)}
											disabled={isSaving || isLoading}
											hasError={!!validationErrors[index]?.halfSleeveWidth}
										/>
									</div>
									<div className="data-size-height">
										<SizeInput
											value={size.halfSleeveHeight}
											onChange={(value) => handleSizeChange(index, 'halfSleeveHeight', value)}
											disabled={isSaving || isLoading}
											hasError={!!validationErrors[index]?.halfSleeveHeight}
										/>
									</div>
								</div>
							))}
						</div>
					</div>

					<div className="artboard--main__sizes--item" id="fullsleeve-sizes">
						<h2>Full Sleeve Sizes <button className="resetSizes" onClick={handleResetFullSleeveSizes} disabled={isSaving || isLoading}><img src={resetIcon}  alt='Reset Button Icon'/>Reset</button></h2>
						<div className="artboard--main__sizes--item--box--head">
							<div className="artboard--main__sizes--item--box--head--list">
								<div className="data-size-width">Width <span>(in)</span></div>
								<div className="data-size-height">Height <span>(in)</span></div>
							</div>
						</div>
						<div className="artboard--main__sizes--item--box--data">
							{localSizes.map((size, index) => (
								<div key={index} className="artboard--main__sizes--item--box--data--list">
									<div className="data-size-width">
										<SizeInput
											value={size.fullSleeveWidth}
											onChange={(value) => handleSizeChange(index, 'fullSleeveWidth', value)}
											disabled={isSaving || isLoading}
											hasError={!!validationErrors[index]?.fullSleeveWidth}
										/>
									</div>
									<div className="data-size-height">
										<SizeInput
											value={size.fullSleeveHeight}
											onChange={(value) => handleSizeChange(index, 'fullSleeveHeight', value)}
											disabled={isSaving || isLoading}
											hasError={!!validationErrors[index]?.fullSleeveHeight}
										/>
									</div>
								</div>
							))}
						</div>
					</div>
				</section>
			)}
		</main>
	);
};

export default SizeSelectorUi;
